import React, { FC } from 'react';

import { Alert, Empty, Flex, Result, Skeleton, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { RetrieveWarehouseSchema, WarehouseCustomerSchema } from '@app/models/wms';
import { isForbiddenError } from '@app/services/apiErrors';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { AccessDenied } from '@components/AccessDenied';
import { Paper } from '@components/Paper';
import { TABS } from '@pages/Warehouses/pages/Single/interfaces';

import { useGetWarehousesQuery } from '../../../../hooks';
import { getWarehouseColumns } from '../../constants';

const PROVIDER_LIST_LIMIT = 1000;

interface ICustomerGroup {
  customer: WarehouseCustomerSchema;
  warehouses: RetrieveWarehouseSchema[];
}

const groupByCustomer = (warehouses: RetrieveWarehouseSchema[]): ICustomerGroup[] => {
  const groups = new Map<number, ICustomerGroup>();
  warehouses.forEach((warehouse) => {
    const group = groups.get(warehouse.customer.id) || { customer: warehouse.customer, warehouses: [] };
    group.warehouses.push(warehouse);
    groups.set(warehouse.customer.id, group);
  });

  return Array.from(groups.values()).sort((a, b) => a.customer.title.localeCompare(b.customer.title));
};

export const ProviderWarehouses: FC = () => {
  const navigate = useNavigate();
  const warehousesT = useTranslation(NSKeys.warehouses);
  const getErrorMessage = useApiErrorMessage();
  const { data, isLoading, error } = useGetWarehousesQuery({ limit: PROVIDER_LIST_LIMIT, offset: 0 });
  const groups = React.useMemo(() => groupByCustomer(data?.items || []), [data]);
  const columns = React.useMemo(() => getWarehouseColumns({ isProvider: true, t: warehousesT.t }), [warehousesT.t]);

  if (isForbiddenError(error)) {
    return <AccessDenied />;
  }

  const renderContent = () => {
    if (isLoading) {
      return <Skeleton active />;
    }
    if (error) {
      return <Result status="error" subTitle={getErrorMessage(error)} />;
    }
    if (!groups.length) {
      return <Empty />;
    }

    return (
      <Flex vertical gap={24}>
        {data && (data.total ?? 0) > data.items.length && (
          <Alert
            type="warning"
            showIcon
            title={warehousesT.t('truncated', { shown: data.items.length, total: data.total })}
          />
        )}
        {groups.map(({ customer, warehouses }) => (
          <Flex key={customer.id} vertical gap={8}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {customer.title}
            </Typography.Title>
            <Table<RetrieveWarehouseSchema>
              dataSource={warehouses}
              rowKey="uid"
              className="tableClassname"
              tableLayout="fixed"
              columns={columns}
              rowClassName="cursorPointer"
              pagination={false}
              onRow={(row) => ({
                onClick: () => !!row?.$permissions?.view && navigate(`${row.uid}/${TABS.SETTINGS}`),
              })}
            />
          </Flex>
        ))}
      </Flex>
    );
  };

  return <Paper style={{ margin: '24px', padding: 24 }}>{renderContent()}</Paper>;
};
